import { formatInTimeZone } from "date-fns-tz";

import { DIAL_BLOCK_ET_ZONE, DIAL_BLOCK_LENGTH_MINUTES } from "./constants";

const DAY_AND_TIME = "EEE, MMM d, h:mm a";
const TIME_ONLY = "h:mm a";
const DAY_KEY = "yyyy-MM-dd";

/** A parsed instant, or null for a missing or unparseable time. */
export const parseInstant = (iso?: string | null): Date | null => {
  if (!iso) {
    return null;
  }

  const ms = Date.parse(iso);

  return Number.isFinite(ms) ? new Date(ms) : null;
};

/** When a block that starts at `start` ends. */
export const getDialBlockEnd = (start: Date): Date =>
  new Date(start.getTime() + DIAL_BLOCK_LENGTH_MINUTES * 60 * 1000);

/** "Mon, Oct 19, 9:00 AM" in `zone`. */
export const formatDayAndTime = (at: Date, zone: string): string =>
  formatInTimeZone(at, zone, DAY_AND_TIME);

/**
 * "Mon, Oct 19, 9:00 AM – 12:30 PM" in `zone`; the end repeats the day only
 * when the block crosses midnight there.
 */
export const formatDialBlockRange = (
  start: Date,
  end: Date,
  zone: string
): string => {
  const sameDay =
    formatInTimeZone(start, zone, DAY_KEY) ===
    formatInTimeZone(end, zone, DAY_KEY);

  return `${formatDayAndTime(start, zone)} – ${formatInTimeZone(
    end,
    zone,
    sameDay ? TIME_ONLY : DAY_AND_TIME
  )}`;
};

/**
 * A block's times for its card in the week grid, whose column already names
 * the ET day: "9:00 AM – 12:30 PM". When the display zone puts the block on
 * another day than its ET column (a caller far east of ET), the day is
 * printed too.
 */
export const formatBlockTimes = (
  start: Date,
  end: Date,
  zone: string
): string => {
  if (
    formatInTimeZone(start, zone, DAY_KEY) !==
    formatInTimeZone(start, DIAL_BLOCK_ET_ZONE, DAY_KEY)
  ) {
    return formatDialBlockRange(start, end, zone);
  }

  const sameDay =
    formatInTimeZone(start, zone, DAY_KEY) ===
    formatInTimeZone(end, zone, DAY_KEY);

  return `${formatInTimeZone(start, zone, TIME_ONLY)} – ${formatInTimeZone(
    end,
    zone,
    sameDay ? TIME_ONLY : DAY_AND_TIME
  )}`;
};

/** The ET calendar date of an instant, YYYY-MM-DD (the week param's shape). */
export const getEtDateKey = (at: Date): string =>
  formatInTimeZone(at, DIAL_BLOCK_ET_ZONE, DAY_KEY);
